import {FC, useEffect, useMemo, useState } from 'react';

// Import css
import './Main.css';

// Import necessary components
import ClassroomSummary from '../ClassroomSummary';
import StaffSummary from '../StaffSummary';
import Header from './Header';
import ClassroomDetails from '../ClassroomDetails';
import HistoricalRatios from '../HistoricalRatios';
import TourButton from '../TourButton';
import NavigationContext from '../../contexts/NavigationContext';
import { TourProvider } from '../../contexts/TourContext';
import AudioAlert from '../AudioAlert';
import GuidedTour from '../GuidedTour';
import TabNav from './TabNav';
import toast, { ToastBar, Toaster } from 'react-hot-toast';

// Import state stores
import { useErrorStore } from '../../store/errorStore';
import { useAlertsStore } from '../../store/alertStore';
import { useAuthStore } from '../../store/authStore';

// Import  custom data hooks
import { 
  useClassroomData, 
  processedClassroomData, 
  understaffedStatus,
  summaryStats 
} from '../../data/classroomData';
import { useQueryClient } from '@tanstack/react-query';

// Define tab types
export type TabType = 'classrooms' | 'staff' | 'details' | 'historical';

const Main: FC = () => { 
  // Initialize state stores      
  const error = useErrorStore.use.error();
  const setError = useErrorStore.use.setError();
  
  const alerts = useAlertsStore.use.alerts();
  const addAlert = useAlertsStore.use.addAlert();
  const removeAlert = useAlertsStore.use.removeAlert();
  
  const user = useAuthStore.use.user();
  const signOut = useAuthStore.use.signOut();

  // Initialize component state 
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('autoRefreshEnabled');
    return true;
  });
  
  const [activeTab, setActiveTab] = useState<TabType>('classrooms');

  // Use our custom hooks for data fetching and processing
  const classroomQuery = useClassroomData({autoRefresh: autoRefreshEnabled});    

  const { sections, specialCategories, classroomOptions } = useMemo(()=> processedClassroomData(classroomQuery.data), [classroomQuery.data]);

  const hasUnderstaffedClassrooms = understaffedStatus(classroomQuery.data);
    

  const queryClient = useQueryClient()
  // Manual refresh function
  const handleRefresh = async () => {
    await queryClient.refetchQueries()
  };
  
  // Toggle auto-refresh and save preference
  const toggleAutoRefresh = () => {
    const newValue = !autoRefreshEnabled;
    setAutoRefreshEnabled(newValue);
    localStorage.setItem('autoRefreshEnabled', newValue.toString());
    
    // If enabling auto-refresh, trigger a refetch
    if (newValue) classroomQuery.refetch();
  };
  
  return (
    <>
      <NavigationContext.Provider value={{ activeTab, setActiveTab }}>
        <div className="app">     
          <Toaster
            position="top-center"
          >
            {(t) => (
              <ToastBar toast={t}>
                {({ icon, message }) => (
                  <>
                    {icon}
                    {message}
                    {t.type === 'error' && (
                      <button
                        onClick={() => toast.dismiss(t.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          marginLeft: 8,
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </>
                )}
              </ToastBar>
            )}
          </Toaster>

          
          <Header user={user} signOut={signOut} />

          {error && (
            <div className="errorBanner">
              Error: {error}
            </div>
          )}
          
          <div className={`main-content-container`}>
            <div className="updated-info-banner">
              Last updated: { classroomQuery.dataUpdatedAt ? new Date(classroomQuery.dataUpdatedAt).toLocaleTimeString() : 'N/A' }
              <div className="refresh-controls">
                { false && (<label className="auto-refresh-toggle">
                  <input
                    type="checkbox"
                    checked={autoRefreshEnabled}
                    onChange={toggleAutoRefresh}
                  />
                  <span className="auto-refresh-label">Auto refresh</span>
                  </label>
                )}
                <button
                  id='refresh-button' 
                  onClick={handleRefresh}
                  className="button"
                  disabled={classroomQuery.isFetching}
                >
                  {classroomQuery.isFetching ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
            <TabNav activeTab={activeTab} setActiveTab={setActiveTab} hasUnderstaffedClassrooms={hasUnderstaffedClassrooms}> 
              <div className={`tab-pane ${activeTab === 'classrooms' ? 'active' : 'inactive'}`}>
                <ClassroomSummary />
              </div>
              
              <div className={`tab-pane ${activeTab === 'staff' ? 'active' : 'inactive'}`}>
                <StaffSummary refreshTrigger={classroomQuery.dataUpdatedAt} />
              </div>
              
              <div className={`tab-pane ${activeTab === 'details' ? 'active' : 'inactive'}`}>
                <ClassroomDetails/>
              </div>
              
              <div className={`tab-pane ${activeTab === 'historical' ? 'active' : 'inactive'} historical-tab full-width`}>
                <HistoricalRatios refreshTrigger={classroomQuery.dataUpdatedAt}/>
              </div>
            </TabNav>
          </div>
        </div>
      </NavigationContext.Provider>
      <TourProvider 
        onStart = {[
          () => handleRefresh(),
          () => {
            const elements = Array.from(document.querySelectorAll('.expand-button')) as HTMLElement[];
            
            // Trigger events on all tooltip containers
            elements.forEach(element => {
              element.dispatchEvent(new CustomEvent('tour-hide', {
                bubbles: true,
                cancelable: true
              }))
            })
          }
        ]}
        onEnd = {[() => handleRefresh()]}
      >
        <GuidedTour />
        <TourButton/>
      </TourProvider>
    </>
  );
};

// Export the component with React Query provider
export default Main